import { Component, inject, OnInit, OnDestroy, Input, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { RouterModule, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IAppState } from '../../store/states/app.state';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationsMenuComponent } from "../notifications-menu/notifications-menu.component";
import { CurrentUserState } from '../../state/current-user.state';
import { AppUserRole } from '../../models/enums/appUserRole.enum';
import { MessageService } from '../../services/message.service';
import { UserSessionTrackerService } from '../../services/user-session-tracker.service';
import { AuthSyncService } from '../../services/auth-sync.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-top',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    RouterModule,
    ProfileMenuComponent,
    MatBadgeModule,
    MatTooltipModule,
    NotificationsMenuComponent
],
  templateUrl: './top.component.html',
  styleUrls: ['./top.component.scss']
})
export class TopComponent implements OnInit, OnDestroy {
  private readonly authSync = inject(AuthSyncService);
  private readonly router = inject(Router);
  private readonly store = inject(Store<IAppState>);
  private readonly currentUserState = inject(CurrentUserState);
  private readonly messageService = inject(MessageService);
  private readonly sessionTracker = inject(UserSessionTrackerService);

  @Input() variant: 'admin' | 'portal' = 'admin';

  @ViewChild(NotificationsMenuComponent) notificationsMenu?: NotificationsMenuComponent;

  public unreadCount = this.messageService.unreadCount;
  public environmentLabel = environment.environmentLabel;

  public username = computed(() => this.currentUserState.user()?.fullName ?? '');

  public userTooltip = computed(() => {
    const user = this.currentUserState.user();
    if (!user) return '';
    const roleText = this.getRoleText(user.role);
    return `${user.fullName}\n${roleText}\n${user.userName}`;
  });

  private getRoleText(role: AppUserRole): string {
    switch (role) {
      case AppUserRole.Admin:
        return 'Administrator';
      case AppUserRole.Leader:
        return 'Leader';
      case AppUserRole.User:
        return 'User';
      case AppUserRole.Customer:
        return 'Customer';
      default:
        return 'User';
    }
  }

  constructor() {
  }

  ngOnInit() {
    if (this.variant === 'admin') {
      // startPolling() does an immediate refresh, then keeps the badge live.
      this.messageService.startPolling();
    }
  }

  ngOnDestroy() {
    this.messageService.stopPolling();
  }

  public logout() {
    this.messageService.stopPolling();
    this.sessionTracker.endCurrentSession();
    this.authSync.logout();
  }

  public onNotificationMenuOpened(): void {
    this.notificationsMenu?.refresh();
  }

  public goHome() {
    this.router.navigate(['/']);
  }
}
